import { FC, useMemo, useRef, useState } from "react";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridFilterModel,
  GridLoadingOverlay,
  GridLogicOperator,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { Alert, Box, Button, Paper, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useHistory } from "react-router-dom";
import __ from "src/utils/Translations";
import { useTableOperators } from "src/utils/hooks/absences/useTableOperators";
import { usePositionsList } from "src/Pages/Company/OrganizationStructure/api/usePositionsList";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructurePositionEdit,
  organizationStructurePositionsExport,
  organizationStructurePositionsImport,
} from "src/utils/RoleBasedSecurity/permissions";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import { buildRsqlFilters } from "src/Pages/Company/OrganizationStructure/utils/buildRsqlFilters";
import { Position } from "src/Pages/Company/OrganizationStructure/Positions/positions.models";
import PositionModal from "src/Pages/Company/OrganizationStructure/Positions/Modal/PositionModal";
import DeletePositionModal from "src/Pages/Company/OrganizationStructure/Positions/Modal/DeletePositionModal";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import DateRangeFilter from "src/Pages/Company/EmployeeManagement/SyncErrors/Table/DateRangeFilter";
import NumericFilter from "src/Pages/Company/OrganizationStructure/Localizations/Table/NumericFilter";
import DataGridToolbar, {
  TableSettings,
} from "src/Pages/Company/OrganizationStructure/sharedComponents/DataGridToolbar/DataGridToolbar";

interface PositionsTableProps {
  companyId: string;
  structureId: string;
  onAddClick?: () => void;
}

const PositionsTable: FC<PositionsTableProps> = ({
  companyId,
  structureId,
  onAddClick,
}) => {
  const { getStringOperators } = useTableOperators();
  const history = useHistory();
  const hasPermissionPositionEdit = usePermission(
    organizationStructurePositionEdit
  );
  const hasExportPositionsEnabled = usePermission(
    organizationStructurePositionsExport
  );
  const hasImportPositionsEnabled = usePermission(
    organizationStructurePositionsImport
  );
  const hasImportExportPositionsTabEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS_IMPORT_EXPORT_TAB
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  const [settings, setSettings] = useState<TableSettings>({
    density: "standard",
  });
  const [showFilters, setShowFilters] = useState(true);
  const [editingPosition, setEditingPosition] = useState<{
    id: string;
    value: string;
  } | null>(null);
  const [deletingPosition, setDeletingPosition] = useState<Position | null>(
    null
  );

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const requestParams = useMemo(
    () => ({
      companyId,
      structureId,
      page: paginationModel.page,
      size: paginationModel.pageSize,
      sort:
        sortModel.map(({ field, sort }) => `${field}:${sort}`).join(",") ||
        undefined,
      filters: buildRsqlFilters(
        filterModel,
        ["createdAt"],
        ["employeesNumber"]
      ),
    }),
    [companyId, structureId, paginationModel, sortModel, filterModel]
  );

  const { data, isFetching, isError, error } = usePositionsList(requestParams);

  const rowCountRef = useRef(data?.totalElements ?? 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  const columns: GridColDef<Position>[] = useMemo(() => {
    const base: GridColDef<Position>[] = [
      {
        field: "value",
        headerName: __("Stanowisko"),
        flex: 1,
        filterOperators: getStringOperators({ labelUT: "Podaj stanowisko" }),
        valueFormatter: (value: string | null) => value ?? "-",
      },
      {
        field: "createdAt",
        headerName: __("Data dodania"),
        flex: 1,
        renderHeaderFilter: (params) => <DateRangeFilter {...params} />,
        valueFormatter: (value: string) =>
          value ? format(new Date(value), "dd.MM.yyyy") : "-",
      },
      {
        field: "employeesNumber",
        headerName: __("Liczba pracowników"),
        flex: 1,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderHeaderFilter: (params) => <NumericFilter {...params} />,
      },
    ];

    if (hasPermissionPositionEdit) {
      base.push({
        field: "actions",
        type: "actions",
        headerName: __("Akcje"),
        flex: 0.5,
        renderCell: (params) => (
          <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
            <ActionButton
              label={__("Edytuj")}
              bgColor="primary.main"
              bgColorHover="primary.dark"
              color="white"
              onClick={() =>
                setEditingPosition({
                  id: params.row.id,
                  value: params.row.value,
                })
              }
              dataTestId={`os__positions-edit-button-${params.row.id}`}
            >
              <EditIcon />
            </ActionButton>
            <ActionButton
              label={__("Usuń")}
              bgColor="error.main"
              bgColorHover="error.dark"
              color="white"
              onClick={() => setDeletingPosition(params.row)}
              dataTestId={`os__positions-delete-button-${params.row.id}`}
            >
              <DeleteIcon />
            </ActionButton>
          </Stack>
        ),
      });
    }

    return base;
  }, [getStringOperators, hasPermissionPositionEdit]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Paper data-testid="positions-table" sx={{ pt: 2, pb: 1, mb: 2 }}>
        <Box sx={{ height: 770, width: "100%" }}>
          {isError && (
            <Alert variant="outlined" color="warning">
              {String(error)}
            </Alert>
          )}
          <DataGrid
            columns={columns}
            rows={data?.content ?? []}
            rowCount={rowCount}
            loading={isFetching}
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            disableEval
            showCellVerticalBorder={false}
            showColumnVerticalBorder={false}
            density={settings.density}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            disableColumnFilter
            headerFilters={showFilters}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            filterDebounceMs={500}
            showToolbar
            slots={{
              headerFilterMenu: null,
              toolbar: () => (
                <DataGridToolbar
                  id="positions"
                  settings={settings}
                  setSettings={setSettings}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  actionButton={
                    <>
                      {hasImportExportPositionsTabEnabled &&
                        (hasImportPositionsEnabled ||
                          hasExportPositionsEnabled) && (
                          <Button
                            variant="outlined"
                            onClick={() =>
                              history.push(
                                `${history.location.pathname}/import-export-positions`
                              )
                            }
                            data-testid="os__positions-import-export-button"
                            sx={{
                              mr: 1,
                              px: "16px",
                              py: "6px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {__("Importuj/eksportuj")}
                          </Button>
                        )}
                      {onAddClick && (
                        <Button
                          variant="contained"
                          onClick={onAddClick}
                          data-testid="os__positions-add-button"
                          sx={{
                            mr: 1,
                            px: "16px",
                            py: "6px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {__("Dodaj stanowisko")}
                        </Button>
                      )}
                    </>
                  }
                />
              ),
              loadingOverlay: () => (
                <GridLoadingOverlay
                  variant="linear-progress"
                  noRowsVariant="skeleton"
                />
              ),
            }}
            slotProps={{
              row: { "data-testid": "positions-table__row" },
              cell: { "data-testid": "positions-table__cell" },
            }}
            localeText={{
              noRowsLabel: __("Brak stanowisk"),
            }}
          />
        </Box>

        {editingPosition && (
          <PositionModal
            companyId={companyId}
            structureId={structureId}
            data={editingPosition}
            onClose={() => setEditingPosition(null)}
            mode="edit"
          />
        )}
        {deletingPosition && (
          <DeletePositionModal
            companyId={companyId}
            structureId={structureId}
            position={deletingPosition}
            onClose={() => setDeletingPosition(null)}
          />
        )}
      </Paper>
    </LocalizationProvider>
  );
};

export default PositionsTable;
